interface ZeusBoltProps {
  className?: string;
  size?: number;
  animated?: boolean;
  particles?: boolean;
}

export function ZeusBolt({ className = "", size = 24, animated = true, particles = false }: ZeusBoltProps) {
  const id = `bolt-${Math.random().toString(36).slice(2, 6)}`;

  const bolt = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        {/* Electric glow gradient */}
        <linearGradient id={`${id}-grad`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fff8e1" />
          <stop offset="30%" stopColor="#ffd54f" />
          <stop offset="60%" stopColor="#ffab00" />
          <stop offset="100%" stopColor="#ff8f00" />
        </linearGradient>
        {/* Inner bright core */}
        <linearGradient id={`${id}-core`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="50%" stopColor="#fff9c4" />
          <stop offset="100%" stopColor="#ffe082" />
        </linearGradient>
        {/* Glow filter */}
        <filter id={`${id}-glow`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
        {/* Outer glow */}
        <filter id={`${id}-outer`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="3" result="blur" />
        </filter>
      </defs>

      {/* Ambient glow behind bolt */}
      <path
        d="M18 2L7 17h7l-2 13L23 14h-7l2-12z"
        fill={`url(#${id}-grad)`}
        filter={`url(#${id}-outer)`}
        opacity="0.5"
      >
        {animated && (
          <animate attributeName="opacity" values="0.3;0.6;0.3" dur="2s" repeatCount="indefinite" />
        )}
      </path>

      {/* Main bolt body */}
      <path
        d="M18 2L7 17h7l-2 13L23 14h-7l2-12z"
        fill={`url(#${id}-grad)`}
        filter={`url(#${id}-glow)`}
      />

      {/* Bright inner core - slightly narrower */}
      <path
        d="M17.2 4.5L9.5 16h5.2l-1.4 10L21 15h-5.2l1.4-10.5z"
        fill={`url(#${id}-core)`}
        opacity="0.85"
      >
        {animated && (
          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
        )}
      </path>

      {/* Hot white center line */}
      <path
        d="M16.5 6L11 16h3.5l-1 8L20 15h-3.5l1-9z"
        fill="white"
        opacity="0.4"
      >
        {animated && (
          <animate attributeName="opacity" values="0.2;0.5;0.2" dur="0.8s" repeatCount="indefinite" />
        )}
      </path>

      {/* Small spark branches */}
      <g stroke="#ffd54f" strokeWidth="0.8" strokeLinecap="round" opacity="0.6">
        <line x1="10" y1="14" x2="7" y2="12">
          {animated && <animate attributeName="opacity" values="0;0.8;0" dur="2.5s" repeatCount="indefinite" />}
        </line>
        <line x1="9" y1="16" x2="5.5" y2="17">
          {animated && <animate attributeName="opacity" values="0;0.6;0" dur="3s" begin="0.5s" repeatCount="indefinite" />}
        </line>
        <line x1="21" y1="13" x2="24.5" y2="11">
          {animated && <animate attributeName="opacity" values="0;0.7;0" dur="2.2s" begin="0.3s" repeatCount="indefinite" />}
        </line>
        <line x1="20" y1="15" x2="25" y2="16">
          {animated && <animate attributeName="opacity" values="0;0.5;0" dur="2.8s" begin="1s" repeatCount="indefinite" />}
        </line>
      </g>

      {/* Tiny electric sparks */}
      <g fill="#ffffff" opacity="0.7">
        <circle cx="7" cy="12" r="0.6">
          {animated && <animate attributeName="opacity" values="0;1;0" dur="2.5s" repeatCount="indefinite" />}
        </circle>
        <circle cx="25" cy="11" r="0.5">
          {animated && <animate attributeName="opacity" values="0;1;0" dur="2.2s" begin="0.3s" repeatCount="indefinite" />}
        </circle>
        <circle cx="5.5" cy="17" r="0.4">
          {animated && <animate attributeName="opacity" values="0;0.8;0" dur="3s" begin="0.8s" repeatCount="indefinite" />}
        </circle>
      </g>
    </svg>
  );

  if (!particles) return bolt;

  return (
    <div className="bolt-crackle relative inline-block" style={{ width: size, height: size }}>
      {bolt}
      <span className="spark" />
      <span className="spark" />
      <span className="spark" />
      <span className="spark" />
      <span className="spark" />
      <span className="spark" />
    </div>
  );
}
